import React, { useState, useEffect } from 'react';
import { Table, Button, Spinner, Alert, Modal, Form, Badge } from 'react-bootstrap';
import { adminApi } from '../../services/adminApi';

interface Source {
  id: string;
  name: string;
  url?: string;
  templateIds: string[];
  availableForAll: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Template {
  id: string;
  title: string;
  description?: string;
  categoryId?: string;
}

const SourceManagement: React.FC = () => {
  const [sources, setSources] = useState<Source[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSource, setSelectedSource] = useState<Source | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    availableForAll: true,
    templateIds: [] as string[],
  });

  useEffect(() => {
    fetchSources();
    fetchTemplates();
  }, []);

  const fetchSources = async () => {
    try {
      const response = await adminApi.getSources();
      setSources(response.data);
    } catch (err: any) {
      setError('Failed to fetch sources');
      console.error('Error fetching sources:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await adminApi.getTemplates();
      setTemplates(response.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedSource(null);
    setFormData({
      name: '',
      url: '',
      availableForAll: true,
      templateIds: [],
    });
    setShowModal(true);
  };

  const handleEdit = (source: Source) => {
    setModalMode('edit');
    setSelectedSource(source);
    setFormData({
      name: source.name,
      url: source.url || '',
      availableForAll: source.availableForAll,
      templateIds: source.templateIds || [],
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (modalMode === 'create') {
        await adminApi.createSource({
          name: formData.name,
          url: formData.url || undefined,
          availableForAll: formData.availableForAll,
          templateIds: formData.availableForAll ? [] : formData.templateIds,
        });
      } else if (selectedSource) {
        await adminApi.updateSource(selectedSource.id, {
          name: formData.name,
          url: formData.url || undefined,
          availableForAll: formData.availableForAll,
          templateIds: formData.availableForAll ? [] : formData.templateIds,
        });
      }
      await fetchSources();
      setShowModal(false);
      setSelectedSource(null);
    } catch (err: any) {
      alert('Failed to save source');
    }
  };

  const handleDelete = (source: Source) => {
    setSelectedSource(source);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedSource) return;

    try {
      await adminApi.deleteSource(selectedSource.id);
      await fetchSources();
      setShowDeleteModal(false);
      setSelectedSource(null);
    } catch (err: any) {
      alert('Failed to delete source');
    }
  };

  const handleTemplateToggle = (templateId: string) => {
    setFormData((prev) => {
      const templateIds = prev.templateIds.includes(templateId)
        ? prev.templateIds.filter((id) => id !== templateId)
        : [...prev.templateIds, templateId];
      return { ...prev, templateIds };
    });
  };

  const getTemplateNames = (templateIds: string[]) => {
    if (!templateIds || templateIds.length === 0) return '-';
    return templateIds
      .map((id) => templates.find((t) => t.id === id)?.title || id)
      .join(', ');
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
          Sources are reference materials that Claude can use when generating responses.
          By default, sources are available for all templates.
        </p>
        <Button variant="primary" size="sm" onClick={handleCreate}>
          Add Source
        </Button>
      </div>

      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th style={{ width: '180px' }}>Name</th>
            <th style={{ width: '200px' }}>URL</th>
            <th style={{ width: '120px' }}>Availability</th>
            <th>Templates</th>
            <th style={{ width: '150px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sources.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center text-muted py-4">
                No sources configured. Click "Add Source" to create one.
              </td>
            </tr>
          ) : (
            sources.map((source) => (
              <tr key={source.id}>
                <td style={{ fontSize: '12px' }}>{source.name}</td>
                <td style={{ fontSize: '12px' }}>
                  {source.url ? (
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: 'break-all' }}
                    >
                      {source.url.length > 40
                        ? source.url.substring(0, 40) + '...'
                        : source.url}
                    </a>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td style={{ fontSize: '12px' }}>
                  {source.availableForAll ? (
                    <Badge bg="success">All Templates</Badge>
                  ) : (
                    <Badge bg="info">
                      {source.templateIds?.length || 0} Templates
                    </Badge>
                  )}
                </td>
                <td style={{ fontSize: '12px' }}>
                  {source.availableForAll
                    ? 'Available for all templates'
                    : getTemplateNames(source.templateIds)}
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleEdit(source)}
                    className="me-2"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(source)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'Add New Source' : 'Edit Source'}
          </Modal.Title>
          <style>{`
            .modal-header .btn-close {
              width: 48px !important;
            }
            .modal-body {
              max-height: calc(100vh - 210px) !important;
              overflow-y: auto !important;
            }
          `}</style>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Source Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Nielsen Norman Group, Material Design Guidelines"
                required
              />
              <Form.Text className="text-muted">
                Enter a descriptive name for this source. This will be included in Claude's system prompt.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Source URL</Form.Label>
              <Form.Control
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="e.g., https://www.nngroup.com/articles/ten-usability-heuristics/"
              />
              <Form.Text className="text-muted">
                Enter the URL for this source. This ensures Claude provides accurate links instead of making them up.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="availableForAll"
                label="Available for all templates"
                checked={formData.availableForAll}
                onChange={(e) =>
                  setFormData({ ...formData, availableForAll: e.target.checked })
                }
              />
              <Form.Text className="text-muted">
                When enabled, this source will be available for all templates.
                Disable to select specific templates.
              </Form.Text>
            </Form.Group>

            {!formData.availableForAll && (
              <Form.Group className="mb-3">
                <Form.Label>Select Templates</Form.Label>
                <div
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.25rem',
                    padding: '0.5rem',
                  }}
                >
                  {templates.length === 0 ? (
                    <p className="text-muted mb-0">No templates available</p>
                  ) : (
                    templates.map((template) => (
                      <Form.Check
                        key={template.id}
                        type="checkbox"
                        id={`template-${template.id}`}
                        label={template.title}
                        checked={formData.templateIds.includes(template.id)}
                        onChange={() => handleTemplateToggle(template.id)}
                        className="mb-2"
                      />
                    ))
                  )}
                </div>
                <Form.Text className="text-muted">
                  Select the templates that should have access to this source.
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            flexWrap: 'nowrap',
          }}
        >
          <style>{`
            .modal-footer .btn {
              min-width: 60px !important;
              max-width: 120px !important;
              width: auto !important;
              white-space: nowrap !important;
              padding: 0.375rem 0.75rem !important;
            }
          `}</style>
          <Button variant="link" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!formData.name.trim()}>
            {modalMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the source "{selectedSource?.name}"? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete Source
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SourceManagement;
